import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import {
  HeartIcon,
  ShareIcon,
  ShoppingCartIcon,
  CheckCircleIcon,
  TruckIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
  MinusIcon,
  PlusIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';
import { RootState } from '@/store';
import { fetchProductById } from '@/store/slices/productsSlice';
import { addToCart, fetchCart } from '@/store/slices/cartSlice';
import { reviewsApi, shopDeliveryOfferApi } from '@/services/api';
import { ROUTES } from '@/utils/constants';
import { getImageUrl, getPlaceholderImage } from '@/utils/imageUtils';
import { formatPrice } from '@/utils/currencyUtils';
import { cn } from '@/utils/cn';
import { ImageGallery } from '@/components/product/ImageGallery';
import { Breadcrumbs } from '@/components/ui/Breadcrumbs';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { RatingStars } from '@/components/ui/RatingStars';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { fadeInUp, staggerContainer, staggerItem } from '@/utils/animations';
import { DeliveryOption, ShopDeliveryOffer } from '@/types';

const ProductDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentProduct, isLoading, error } = useSelector((state: RootState) => state.products);
  const { isLoading: isCartLoading } = useSelector((state: RootState) => state.cart);

  const [quantity, setQuantity] = useState(1);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [ratingStats, setRatingStats] = useState<{
    average_rating: number;
    total_reviews: number;
  } | null>(null);
  const [shopOffers, setShopOffers] = useState<ShopDeliveryOffer[]>([]);

  useEffect(() => {
    if (id) {
      dispatch(fetchProductById(Number(id)));
    }
  }, [dispatch, id]);

  useEffect(() => {
    const fetchRating = async () => {
      if (currentProduct) {
        try {
          const response = await reviewsApi.getProductStats(currentProduct.id);
          if (response.data) {
            setRatingStats({
              average_rating: response.data.average_rating || 0,
              total_reviews: response.data.total_reviews || 0,
            });
          }
        } catch (error) {
          // Silently fail
        }
      }
    };
    fetchRating();
  }, [currentProduct]);

  // Fetch shop offers when product loads
  useEffect(() => {
    const fetchShopOffers = async () => {
      if (currentProduct && currentProduct.shop_id) {
        try {
          const response = await shopDeliveryOfferApi.getShopOffers(currentProduct.shop_id, true);
          setShopOffers(response.data);
        } catch (error) {
          // Silently fail - offers are optional
          setShopOffers([]);
        }
      }
    };
    fetchShopOffers();
  }, [currentProduct]);

  const handleAddToCart = async () => {
    if (!currentProduct) return;

    try {
      await dispatch(
        addToCart({
          product_id: currentProduct.id,
          quantity: quantity,
          selected_delivery_option_id: 0, // Delivery option will be selected at checkout
        })
      ).unwrap();
      dispatch(fetchCart());
      toast.success('Added to cart!');
    } catch (error) {
      console.error('Failed to add to cart:', error);
      toast.error('Failed to add to cart');
    }
  };

  const handleBuyNow = async () => {
    if (!currentProduct) return;

    try {
      await dispatch(
        addToCart({
          product_id: currentProduct.id,
          quantity: quantity,
          selected_delivery_option_id: 0, // Delivery option will be selected at checkout
        })
      ).unwrap();
      navigate('/dashboard/buyer/checkout');
    } catch (error) {
      console.error('Failed to buy now:', error);
      toast.error('Failed to proceed to checkout');
    }
  };

  const toggleWishlist = () => {
    setIsWishlisted(!isWishlisted);
    toast.success(isWishlisted ? 'Removed from wishlist' : 'Added to wishlist');
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard!');
  };

  const increaseQuantity = () => {
    if (currentProduct && currentProduct.inventory && quantity < currentProduct.inventory.current_stock) {
      setQuantity(quantity + 1);
    }
  };

  const decreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400 font-medium">Loading product...</p>
        </div>
      </div>
    );
  }

  if (error || !currentProduct) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-900">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Product not found
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {error || 'The product you are looking for does not exist.'}
          </p>
          <Button onClick={() => navigate(ROUTES.PRODUCTS)}>Back to Products</Button>
        </div>
      </div>
    );
  }

  const product = currentProduct;
  const averageRating = ratingStats?.average_rating || 0;
  const totalReviews = ratingStats?.total_reviews || 0;
  const discount =
    product.original_price && product.original_price > product.price
      ? Math.round(((product.original_price - product.price) / product.original_price) * 100)
      : undefined;
  const isInStock = product.inventory?.is_in_stock || (product.inventory?.current_stock || 0) > 0;
  const isLowStock = product.inventory?.is_low_stock || ((product.inventory?.current_stock || 0) > 0 && (product.inventory?.current_stock || 0) <= 5);

  // Prepare images for ImageGallery
  const imageUrls =
    product.images && product.images.length > 0
      ? product.images.map((img) => getImageUrl(img))
      : [getPlaceholderImage(600, 600, 'No Image')];

  // Breadcrumb items
  const breadcrumbItems = [
    { label: 'Home', path: ROUTES.HOME },
    { label: 'Products', path: ROUTES.PRODUCTS },
    ...(product.category_name
      ? [{ label: product.category_name, path: `${ROUTES.PRODUCTS}?category=${product.category?.id}` }]
      : []),
    { label: product.name, path: '' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900">
      {/* Breadcrumbs */}
      <div className="bg-white dark:bg-dark-850 border-b border-gray-200 dark:border-dark-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Main Product Section */}
        <motion.div
          initial="initial"
          animate="animate"
          variants={staggerContainer}
          className="bg-white dark:bg-dark-800 rounded-2xl border border-gray-200 dark:border-dark-700 overflow-hidden"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-6 lg:p-8">
            {/* Left Side - Image Gallery */}
            <motion.div variants={staggerItem}>
              <ImageGallery images={imageUrls} alt={product.name} />
            </motion.div>

            {/* Right Side - Product Info */}
            <motion.div variants={staggerItem} className="flex flex-col">
              {/* Title */}
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4 leading-tight">
                {product.name}
              </h1>

              {/* Brand */}
              {product.brand && (
                <p className="text-sm text-primary-600 dark:text-primary-400 hover:underline cursor-pointer mb-4">
                  Visit the {product.brand} Store
                </p>
              )}

              {/* Rating & Reviews */}
              {totalReviews > 0 && (
                <div className="flex items-center gap-3 mb-4">
                  <RatingStars rating={averageRating} size="md" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {averageRating.toFixed(1)} ({totalReviews.toLocaleString()} reviews)
                  </span>
                </div>
              )}

              {/* Badges */}
              <div className="flex flex-wrap gap-2 mb-6">
                {product.product_type === 'recycled' && (
                  <Badge variant="success" className="text-sm">
                    Recycled Product
                  </Badge>
                )}
                {product.product_type === 'brand_new' && (
                  <Badge variant="info" className="text-sm">
                    Brand New
                  </Badge>
                )}
                {discount && (
                  <Badge variant="error" className="text-sm">
                    {discount}% OFF
                  </Badge>
                )}
                {product.warranty && (
                  <Badge variant="default" className="text-sm">
                    Warranty Included
                  </Badge>
                )}
              </div>

              {/* Price */}
              <div className="mb-6 pb-6 border-b border-gray-200 dark:border-dark-700">
                <div className="flex items-baseline gap-3 mb-2">
                  <span className="text-4xl font-bold text-gray-900 dark:text-white">
                    {formatPrice(product.price)}
                  </span>
                  {product.original_price && product.original_price > product.price && (
                    <>
                      <span className="text-xl text-gray-500 dark:text-gray-400 line-through">
                        {formatPrice(product.original_price)}
                      </span>
                      <span className="text-sm text-success-600 dark:text-success-400 font-semibold">
                        Save {formatPrice(product.original_price - product.price)}
                      </span>
                    </>
                  )}
                </div>
                {/* Stock Status */}
                <div className="flex items-center gap-2">
                  {isInStock ? (
                    <>
                      <CheckCircleIcon className="h-5 w-5 text-success-500" />
                      <span className="text-success-600 dark:text-success-400 font-medium">
                        In Stock
                        {isLowStock && product.inventory && ` - Only ${product.inventory.current_stock} left!`}
                      </span>
                    </>
                  ) : (
                    <span className="text-error-600 dark:text-error-400 font-medium">
                      Out of Stock
                    </span>
                  )}
                </div>
              </div>

              {/* Quick Details */}
              <div className="space-y-3 mb-6">
                {product.brand && (
                  <div className="flex items-start">
                    <span className="text-sm text-gray-600 dark:text-gray-400 w-24">Brand:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {product.brand}
                    </span>
                  </div>
                )}
                {product.model && (
                  <div className="flex items-start">
                    <span className="text-sm text-gray-600 dark:text-gray-400 w-24">Model:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {product.model}
                    </span>
                  </div>
                )}
                {product.condition && (
                  <div className="flex items-start">
                    <span className="text-sm text-gray-600 dark:text-gray-400 w-24">Condition:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                      {product.condition}
                    </span>
                  </div>
                )}
                {product.shop && (
                  <div className="flex items-start">
                    <span className="text-sm text-gray-600 dark:text-gray-400 w-24">Sold by:</span>
                    <span className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline cursor-pointer">
                      {product.shop.name}
                    </span>
                  </div>
                )}
              </div>

              {/* Delivery Information - Selection happens at checkout */}
              {product.delivery_options && product.delivery_options.length > 0 && (
                <div className="mb-6 pb-6 border-b border-gray-200 dark:border-dark-700">
                  <div className="flex items-center text-sm font-medium text-gray-900 dark:text-white mb-3">
                    <TruckIcon className="h-5 w-5 mr-2" />
                    Delivery Available
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {product.delivery_options.some(opt => opt.is_free_delivery) ? (
                      <span className="text-green-600 dark:text-green-400 font-medium">Free delivery available</span>
                    ) : (
                      <span>Starting from {formatPrice(Math.min(...product.delivery_options.map(opt => opt.delivery_charge || 0)))}</span>
                    )}
                    {' • '}Choose delivery option at checkout
                  </div>

                  {/* Shop Delivery Offers */}
                  {shopOffers.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {shopOffers.slice(0, 2).map((offer) => (
                        <div
                          key={offer.id}
                          className="flex items-start p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg"
                        >
                          <CheckCircleIcon className="h-4 w-4 text-green-600 dark:text-green-400 mt-0.5 mr-2 flex-shrink-0" />
                          <p className="text-xs text-green-800 dark:text-green-300">
                            {offer.offer_name}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Quantity Selector */}
              {isInStock && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-900 dark:text-white mb-3">
                    Quantity
                  </label>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center border-2 border-gray-300 dark:border-dark-600 rounded-lg">
                      <button
                        onClick={decreaseQuantity}
                        disabled={quantity <= 1}
                        className={cn(
                          'p-3 hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors',
                          'disabled:opacity-50 disabled:cursor-not-allowed'
                        )}
                      >
                        <MinusIcon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
                      </button>
                      <span className="px-6 text-lg font-semibold text-gray-900 dark:text-white">
                        {quantity}
                      </span>
                      <button
                        onClick={increaseQuantity}
                        disabled={quantity >= (product.inventory?.current_stock || 0)}
                        className={cn(
                          'p-3 hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors',
                          'disabled:opacity-50 disabled:cursor-not-allowed'
                        )}
                      >
                        <PlusIcon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
                      </button>
                    </div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {product.inventory?.current_stock || 0} available
                    </span>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="space-y-3 mb-6">
                <Button
                  size="lg"
                  variant="primary"
                  fullWidth
                  onClick={handleAddToCart}
                  disabled={!isInStock || isCartLoading}
                  leftIcon={<ShoppingCartIcon className="h-5 w-5" />}
                >
                  {isInStock ? 'Add to Cart' : 'Out of Stock'}
                </Button>
                <Button
                  size="lg"
                  variant="accent"
                  fullWidth
                  onClick={handleBuyNow}
                  disabled={!isInStock || isCartLoading}
                >
                  Buy Now
                </Button>
              </div>

              {/* Secondary Actions */}
              <div className="flex items-center gap-4 pb-6 border-b border-gray-200 dark:border-dark-700">
                <button
                  onClick={toggleWishlist}
                  className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  {isWishlisted ? (
                    <HeartSolidIcon className="h-5 w-5 text-red-500" />
                  ) : (
                    <HeartIcon className="h-5 w-5" />
                  )}
                  {isWishlisted ? 'Saved' : 'Save'}
                </button>
                <button
                  onClick={handleShare}
                  className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  <ShareIcon className="h-5 w-5" />
                  Share
                </button>
              </div>

              {/* Trust Badges */}
              <div className="grid grid-cols-3 gap-4 mt-6">
                <div className="text-center">
                  <TruckIcon className="h-8 w-8 mx-auto text-primary-500 dark:text-primary-400 mb-2" />
                  <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                    Free Delivery
                  </p>
                </div>
                <div className="text-center">
                  <ArrowPathIcon className="h-8 w-8 mx-auto text-primary-500 dark:text-primary-400 mb-2" />
                  <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                    Easy Returns
                  </p>
                </div>
                <div className="text-center">
                  <ShieldCheckIcon className="h-8 w-8 mx-auto text-primary-500 dark:text-primary-400 mb-2" />
                  <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                    Secure Payment
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Product Details Tabs */}
          <div className="border-t border-gray-200 dark:border-dark-700 p-6 lg:p-8">
            <Tabs defaultValue="description">
              <TabsList variant="underline">
                <TabsTrigger value="description">Description</TabsTrigger>
                <TabsTrigger value="specifications">Specifications</TabsTrigger>
                <TabsTrigger value="reviews">Reviews ({totalReviews})</TabsTrigger>
              </TabsList>

              <TabsContent value="description">
                <div className="py-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    About this item
                  </h3>
                  <div className="prose dark:prose-invert max-w-none">
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">
                      {product.description || 'No description available.'}
                    </p>
                  </div>
                  {product.location && (
                    <div className="mt-6 p-4 bg-gray-50 dark:bg-dark-700 rounded-lg">
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                        Location
                      </h4>
                      <p className="text-gray-700 dark:text-gray-300">
                        {product.location.hierarchy
                          ? product.location.hierarchy.map((loc: any) => loc.name).join(', ')
                          : product.location.name}
                      </p>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="specifications">
                <div className="py-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {[
                      { label: 'Product Type', value: product.product_type.replace('_', ' ') },
                      { label: 'Category', value: product.category_name },
                      { label: 'Brand', value: product.brand },
                      { label: 'Model', value: product.model },
                      { label: 'Condition', value: product.condition },
                      { label: 'Warranty', value: product.warranty },
                      { label: 'Shop', value: product.shop?.name },
                    ]
                      .filter((spec) => spec.value)
                      .map((spec, index) => (
                        <div
                          key={index}
                          className="flex items-start p-4 bg-gray-50 dark:bg-dark-700 rounded-lg"
                        >
                          <div className="flex-1">
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                              {spec.label}
                            </p>
                            <p className="text-base font-medium text-gray-900 dark:text-white capitalize">
                              {spec.value}
                            </p>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="reviews">
                <div className="py-6">
                  {totalReviews > 0 ? (
                    <div>
                      <div className="flex items-center gap-6 mb-8">
                        <div className="text-center">
                          <div className="text-5xl font-bold text-gray-900 dark:text-white mb-2">
                            {averageRating.toFixed(1)}
                          </div>
                          <RatingStars rating={averageRating} size="lg" />
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                            {totalReviews} reviews
                          </p>
                        </div>
                      </div>
                      <p className="text-gray-600 dark:text-gray-400">
                        Full review display coming soon.
                      </p>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-gray-600 dark:text-gray-400 mb-4">
                        No reviews yet. Be the first to review this product!
                      </p>
                      <Button variant="outline">Write a Review</Button>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
